import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";

const FormContactSection = ({ currentData, onSubmit, isLoading }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const [headerTitleAr, setHeaderTitleAr] = useState("");
    const [headerTitleEn, setHeaderTitleEn] = useState("");
    const [headerSubtitleAr, setHeaderSubtitleAr] = useState("");
    const [headerSubtitleEn, setHeaderSubtitleEn] = useState("");
    const [headerDescriptionAr, setHeaderDescriptionAr] = useState("");
    const [headerDescriptionEn, setHeaderDescriptionEn] = useState("");

    const [image, setImage] = useState(null);

    useEffect(() => {
        if (currentData) {
            setHeaderTitleAr(currentData?.header?.title?.ar || "");
            setHeaderTitleEn(currentData?.header?.title?.en || "");
            setHeaderSubtitleAr(currentData?.header?.subtitle?.ar || "");
            setHeaderSubtitleEn(currentData?.header?.subtitle?.en || "");
            setHeaderDescriptionAr(currentData?.header?.description?.ar || "");
            setHeaderDescriptionEn(currentData?.header?.description?.en || "");

            setImage(currentData?.image || null);
        }
    }, [currentData]);

    const getImageName = (imageVal) => {
        if (!imageVal) return "";
        if (typeof imageVal === 'string') {
            return imageVal.substr(imageVal.lastIndexOf('/') + 1);
        }
        return imageVal;
    };

    const handleImageChange = (e) => {
        setImage(e.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            header: {
                title: {
                    ar: headerTitleAr || headerTitleEn,
                    en: headerTitleEn || headerTitleAr,
                },
                subtitle: {
                    ar: headerSubtitleAr || headerSubtitleEn,
                    en: headerSubtitleEn || headerSubtitleAr,
                },
                description: {
                    ar: headerDescriptionAr || headerDescriptionEn,
                    en: headerDescriptionEn || headerDescriptionAr,
                },
            },
            image: getImageName(image),
        };

        onSubmit(payload);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isArabic ? "رأس الصفحة" : "Header"}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <FormInput
                            type="file"
                            name="image"
                            labelImage={isArabic ? "صورة البانر" : "Banner Image"}
                            value={image}
                            onChange={handleImageChange}
                            width={200}
                            height={120}
                        />
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان رأس الصفحة (عربي)"
                                placeholder="أدخل عنوان رأس الصفحة بالعربي"
                                value={headerTitleAr}
                                onChange={(e) => setHeaderTitleAr(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Header Title (English)"
                                placeholder="Enter header title in English"
                                value={headerTitleEn}
                                onChange={(e) => setHeaderTitleEn(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الفرعي لرأس الصفحة (عربي)"
                                placeholder="أدخل العنوان الفرعي لرأس الصفحة بالعربي"
                                value={headerSubtitleAr}
                                onChange={(e) => setHeaderSubtitleAr(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Header Subtitle (English)"
                                placeholder="Enter header subtitle in English"
                                value={headerSubtitleEn}
                                onChange={(e) => setHeaderSubtitleEn(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                وصف رأس الصفحة (عربي)
                            </Typography>
                            <TextEditor
                                name="header_description_ar"
                                value={headerDescriptionAr}
                                onChange={(e) => setHeaderDescriptionAr(e.target.value)}
                                placeholder="أدخل وصف رأس الصفحة بالعربي"
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Header Description (English)
                            </Typography>
                            <TextEditor
                                name="header_description_en"
                                value={headerDescriptionEn}
                                onChange={(e) => setHeaderDescriptionEn(e.target.value)}
                                placeholder="Enter header description in English"
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{ minWidth: 120 }}
                >
                    {isLoading ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                </Button>
            </Box>
        </Box>
    );
};

export default FormContactSection;